import { useEffect } from 'react';
import {
  findConsumingVerticalScroller,
  findNearestVerticalScroller,
} from '../../../utils/touchScroll';

/**
 * Locks pull-to-refresh / rubber-band overscroll while a pick view is mounted.
 * History swipe is handled app-wide by `useDisableHistoryGestures`.
 * Nested scrollers (pick list, scan-queue chips, tables) keep native pan.
 */

/** Overlays that own their own scrolling and must keep native touch behavior. */
const OVERLAY_SELECTOR =
  '[role="dialog"], .pbl-modal, .menu, .scanner-action-menu-panel, .order-detail-complete-sheet';

export function usePickViewTouchLock(): void {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add('order-detail-lock-refresh');
    body.classList.add('order-detail-lock-refresh');

    let touchStartX = 0;
    let touchStartY = 0;

    const onTouchStart = (event: TouchEvent): void => {
      const touch = event.touches[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    };

    const onTouchMove = (event: TouchEvent): void => {
      const touch = event.touches[0];
      if (!touch) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(OVERLAY_SELECTOR)) return;

      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        return;
      }

      const scroller = findNearestVerticalScroller(target);
      if (scroller) {
        if (findConsumingVerticalScroller(target, deltaY) === null && event.cancelable) {
          event.preventDefault();
        }
        return;
      }
      if (deltaY > 0 && event.cancelable) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      html.classList.remove('order-detail-lock-refresh');
      body.classList.remove('order-detail-lock-refresh');
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, []);
}

export default usePickViewTouchLock;
